// src/lib/supabaseClient.js
// Supabase client initialization and shared constants for MYKLUBB

import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

// =====================================================
// Org activa (aislamiento duro server-side)
// =====================================================
// Cada peticion Supabase lleva el header 'x-active-org' con la org que el
// usuario esta gestionando. El RLS (current_active_org) lo VALIDA contra las
// membresias y clava TODAS las queries a esa unica org. Si no se manda, el RLS
// cae a la org principal. El header lo fija OrganizationContext al resolver la
// org activa (ver setActiveOrgId).
let activeOrgId = null

/** Fija la org activa que viajara en el header de cada peticion. */
export const setActiveOrgId = (orgId) => {
  activeOrgId = orgId || null
}

/** La org activa que viaja en el header (la fija OrganizationContext; con override de Platform Admin, la del override). */
export const getActiveOrgId = () => activeOrgId

const fetchWithActiveOrg = (input, init = {}) => {
  const headers = new Headers(init.headers || {})
  if (activeOrgId) headers.set('x-active-org', activeOrgId)
  return fetch(input, { ...init, headers })
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  global: { fetch: fetchWithActiveOrg },
})

export const DEFAULT_ORG_ID = '11111111-1111-1111-1111-111111111111'
/** @deprecated Use DEFAULT_ORG_ID instead */
export const DEFAULT_CLUB_ID = DEFAULT_ORG_ID

export const EVENTS_ENABLED = true

// Bucket for training program assets
export const TRAINING_BUCKET = process.env.REACT_APP_TRAINING_BUCKET || 'training-plans'

// Platform config singleton ID
export const PLATFORM_CONFIG_ID = '00000000-0000-0000-0000-000000000001'

// =====================================================
// Tenant context helpers
// =====================================================
// Para multi-tenancy: cuando un platform_admin esta "viendo como"
// otra organizacion, las queries deben filtrar por esa org_id en
// vez de bypass total. El frontend guarda el override en sessionStorage
// y los services lo leen automaticamente via estos helpers.

const ORG_OVERRIDE_KEY = 'myklubb_org_override'

/**
 * Devuelve el orgId del override activo (platform admin impersonando)
 * o null si no hay override. Se usa como filtro automatico en services.
 */
export const getActiveOrgOverride = () => {
  if (typeof window === 'undefined') return null
  try {
    return window.sessionStorage.getItem(ORG_OVERRIDE_KEY) || null
  } catch {
    return null
  }
}

/**
 * Limpia el override de org. Debe llamarse al cerrar sesion: sessionStorage
 * sobrevive al logout en la misma pestana, y un override heredado hace que
 * applyOrgFilter filtre las queries del siguiente usuario por una org ajena.
 */
export const clearActiveOrgOverride = () => {
  setActiveOrgId(null)
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(ORG_OVERRIDE_KEY)
  } catch {
    // sessionStorage inaccesible: no hay override que limpiar
  }
}

/**
 * Aplica filtro org_id a una query Supabase.
 * - Si se pasa explicitOrgId: filtra por ese
 * - Si hay override activo: filtra por el override (impersonation)
 * - Si no: no filtra (RLS hara su trabajo segun el rol del usuario)
 *
 * Uso en services:
 *   let query = supabase.from('grupos').select('*')
 *   query = applyOrgFilter(query, explicitOrgId)
 */
export const applyOrgFilter = (query, explicitOrgId = null) => {
  const orgId = explicitOrgId || getActiveOrgOverride()
  if (orgId) {
    return query.eq('org_id', orgId)
  }
  return query
}

// =====================================================
// Imagenes de jugadores/staff (bucket privado)
// =====================================================
// El bucket 'jugadores-images' es PRIVADO (BI-2). Las fotos no son URLs
// publicas: se firman bajo demanda. signImagesDeep recorre la data devuelta
// por un service y, para cada objeto que tenga `imagen_path`, reemplaza
// `imagen_url` por una signed URL temporal. Hace batch para minimizar llamadas.

export const PLAYER_IMAGES_BUCKET = 'jugadores-images'

export const signImagesDeep = async (data, expiresIn = 3600) => {
  if (!data) return data
  const targets = []
  const seen = new Set()
  const walk = (node) => {
    if (!node || typeof node !== 'object' || seen.has(node)) return
    seen.add(node)
    if (Array.isArray(node)) {
      node.forEach(walk)
      return
    }
    if (typeof node.imagen_path === 'string' && node.imagen_path) {
      targets.push(node)
    }
    for (const key in node) {
      const val = node[key]
      if (val && typeof val === 'object') walk(val)
    }
  }
  walk(data)

  if (!targets.length) return data

  const paths = [...new Set(targets.map((t) => t.imagen_path))]
  try {
    const { data: signed, error } = await supabase
      .storage
      .from(PLAYER_IMAGES_BUCKET)
      .createSignedUrls(paths, expiresIn)
    if (error || !signed) return data
    const urlByPath = new Map(signed.map((s) => [s.path, s.signedUrl]))
    for (const t of targets) {
      const url = urlByPath.get(t.imagen_path)
      if (url) t.imagen_url = url
    }
  } catch {
    // si falla la firma, dejamos imagen_url como este (posiblemente null)
  }
  return data
}
